import {
  Group,
  Table as MantineTable,
  TableTbody,
  TableTd,
  TableTh,
  TableThead,
  TableTr,
  Text,
  Alert,
  TableCaption,
  TableProps as MantineTableProps,
  useMantineTheme,
  Overlay,
} from "@mantine/core"
import { IconInfoCircle } from "@tabler/icons-react"
import { flexRender, Table as TableType } from "@tanstack/react-table"

import {
  CHECKBOX_COLUMN_ID,
  getCommonPinningStyles,
  Loader,
  Pagination,
  PINNED_POSITIONS,
  ScrollArea,
  Select,
} from "@tfg/ui"

import classes from "./Table.module.css"
import { TableHeaderCell } from "./TableHeaderCell"

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50]
export const DEFAULT_PAGE_SIZE = 20

export type TableProps<T> = {
  table: TableType<T>
  onRowClick?: (row: T) => void
  view?: string
  pagination?: boolean
  pageSizeOptions?: number[]
  manualPaginationCount?: number
  translations: Record<keyof typeof TableTranslationsKeys, string>
  stickyHeader?: boolean
  tableCaption?: string
  h?: string | number
  mah?: string | number
  w?: string | number
  maw?: string | number
  setOpenColumnManager?: React.Dispatch<React.SetStateAction<boolean>>
  isFetching?: boolean
} & MantineTableProps

export const TableTranslationsKeys = {
  noDataMessage: "No results found",
  noDataTitle: "No data found",
  total: "Total: ",
  rowsPerPage: "Rows per page",
  pinColumn: "Pin column",
  unpinColumn: "Unpin column",
  hideColumn: "Hide column",
  showColumn: "Show column",
  manageColumns: "Manage columns",
} as const

export const Table = <T,>({
  table,
  onRowClick,
  pagination = true,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  manualPaginationCount,
  translations,
  stickyHeader = false,
  tableCaption,
  mah = "var(--table-height)",
  h = "100%",
  w = "100%",
  setOpenColumnManager,
  isFetching = false,
  ...props
}: TableProps<T>) => {
  const tableState = table.getState()
  const theme = useMantineTheme()

  const tableContent = (
    <>
      <ScrollArea
        h={h}
        mah={mah}
        w={w}
        className={classes.scrollArea}
        styles={{ viewport: { maxHeight: mah } }}
      >
        <MantineTable
          stickyHeader={stickyHeader}
          classNames={classes}
          withRowBorders
          layout="fixed"
          {...props}
        >
          <TableThead>
            <TableTr>
              {table
                .getLeftLeafHeaders()
                .concat(table.getCenterLeafHeaders())
                .map((header) => {
                  const isPinned = header.column.getIsPinned() === "left"
                  const size = header.getContext().column.getSize()
                  const minWidth = header.column.columnDef.minSize
                  const maxWidth = header.column.columnDef.maxSize
                  return (
                    <TableTh
                      key={`${header.id}-header`}
                      w={
                        header.column.id === CHECKBOX_COLUMN_ID
                          ? theme.other.table.cells.checkbox
                          : header.column.id.startsWith("action-cell-")
                            ? theme.other.table.cells.action
                            : size
                      }
                      miw={
                        header.column.id === CHECKBOX_COLUMN_ID ||
                        header.column.id.startsWith("action-cell-")
                          ? theme.other.table.cells.action
                          : minWidth
                      }
                      data-pinned={isPinned}
                      maw={maxWidth}
                      data-last-left-pinned={header.column.getIsLastColumn(
                        PINNED_POSITIONS.LEFT
                      )}
                      data-last-right-pinned={header.column.getIsLastColumn(
                        PINNED_POSITIONS.RIGHT
                      )}
                      data-action-cell={header.column.id.startsWith(
                        "action-cell"
                      )}
                      style={{
                        ...(isPinned
                          ? getCommonPinningStyles(header.column)
                          : undefined),
                        ...(header.column.id === CHECKBOX_COLUMN_ID
                          ? { zIndex: 2 }
                          : undefined),
                      }}
                      onClick={() =>
                        header.column.toggleSorting(undefined, true)
                      }
                    >
                      <TableHeaderCell
                        key={header.id}
                        header={header}
                        setOpenColumnManager={setOpenColumnManager}
                        translations={translations}
                      />
                    </TableTh>
                  )
                })}
            </TableTr>
          </TableThead>
          <TableTbody>
            {table.getRowModel().rows.map((row) => (
              <TableTr
                key={row.id}
                onClick={() => onRowClick?.(row.original)}
                style={{
                  cursor: onRowClick ? "pointer" : "default",
                }}
              >
                {row
                  .getLeftVisibleCells()
                  .concat(row.getCenterVisibleCells())
                  .map((cell) => {
                    const isPinned = cell.column.getIsPinned() === "left"
                    return (
                      <TableTd
                        key={cell.id}
                        data-pinned={isPinned}
                        data-last-left-pinned={cell.column.getIsLastColumn(
                          PINNED_POSITIONS.LEFT
                        )}
                        data-last-right-pinned={cell.column.getIsLastColumn(
                          PINNED_POSITIONS.RIGHT
                        )}
                        style={{
                          ...(isPinned
                            ? getCommonPinningStyles(cell.column)
                            : undefined),
                          ...(cell.column.id === CHECKBOX_COLUMN_ID
                            ? { zIndex: 2 }
                            : undefined),
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableTd>
                    )
                  })}
              </TableTr>
            ))}

            {table.getRowModel().rows.length === 0 && (
              <TableTr>
                <TableTd colSpan={table.getAllLeafColumns().length}>
                  <Group
                    mt={40}
                    mb={40}
                    style={{ marginLeft: 40 }}
                    mih={100}
                    maw="90vw"
                    h="100%"
                    justify="center"
                    align="center"
                  >
                    <Alert
                      bg="transparent"
                      color="gray"
                      icon={<IconInfoCircle />}
                      title={
                        translations.noDataTitle ??
                        TableTranslationsKeys.noDataTitle
                      }
                      style={{ width: "fit-content", textAlign: "center" }}
                    >
                      {translations.noDataMessage ??
                        TableTranslationsKeys.noDataMessage}
                    </Alert>
                  </Group>
                </TableTd>
              </TableTr>
            )}
          </TableTbody>
          {tableCaption && <TableCaption>{tableCaption}</TableCaption>}
        </MantineTable>
      </ScrollArea>
      {pagination ? (
        <Group justify="space-between">
          <Text fw={500}>
            {translations.total ?? TableTranslationsKeys.total}
            <Text span fw={700}>
              {manualPaginationCount
                ? manualPaginationCount
                : table.getRowCount()}
            </Text>
          </Text>

          <Group justify="right">
            <Pagination
              value={tableState.pagination.pageIndex + 1}
              total={table.getPageCount()}
              onChange={(page) => {
                table.setPageIndex(page - 1)
              }}
            />
          </Group>
          <Group gap="xs">
            <Text fw={500}>
              {translations.rowsPerPage ?? TableTranslationsKeys.rowsPerPage}
            </Text>

            <Select
              size="xs"
              data={pageSizeOptions.map((page) => page.toString())}
              value={tableState.pagination.pageSize.toString()}
              onChange={(pageSize) => {
                table.setPageSize(Number(pageSize))
              }}
              w={60}
            />
          </Group>
        </Group>
      ) : null}
    </>
  )

  return isFetching ? (
    <Overlay
      mah="var(--table-height)"
      h="fit-content"
      opacity={0.2}
      zIndex={100}
      radius="md"
      pos="relative"
    >
      <Loader
        pos="absolute"
        style={{
          zIndex: 100,
          m: "auto",
          w: "100%",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      />
      {tableContent}
    </Overlay>
  ) : (
    tableContent
  )
}
